import {
  FieldValues,
  Path,
  Resolver,
  SubmitHandler,
  useForm
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormDefinition, InputDefinition } from './interface'
import styles from './styles.module.scss'
import { TextInput } from '../../components/UI/ReactHookForm/TextInput/TextInput'
import { ButtonByAdam } from '../../components/UI/buttons/ButtonByAdam/ButtonByAdam'
import { DefaultHeader } from '../../components/UI/headers/DefaultHeader/DefaultHeader'

export type InputsGeneratorProps<TFormValues extends FieldValues> = {
  formDefinition: FormDefinition
  onSubmit: SubmitHandler<TFormValues>
}

export const RHFSimpleFormBuilder = <TFormValues extends FieldValues>({
  formDefinition,
  onSubmit
}: InputsGeneratorProps<TFormValues>): JSX.Element => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<TFormValues>({
    resolver: zodResolver(formDefinition.zodSchema) as Resolver<TFormValues>
  })
  const renderInput = (input: InputDefinition) => {
    if (input.inputType === 'text') {
      return (
        <TextInput<TFormValues>
          key={input.id}
          name={input.name as Path<TFormValues>}
          label={input.label}
          placeholder={input.placeholder}
          autoComplete={input.autoComplete}
          errors={errors}
          register={register}
        />
      )
    }
    if (input.inputType === 'password') {
      return (
        <TextInput<TFormValues>
          type='password'
          key={input.id}
          name={input.name as Path<TFormValues>}
          label={input.label}
          placeholder={input.placeholder}
          autoComplete={input.autoComplete}
          errors={errors}
          register={register}
        />
      )
    }

    throw Error('invalid form schema')
  }

  const renderInputs = () =>
    formDefinition.inputs.map((input) => renderInput(input))

  return (
    <section className={styles.FormContainer}>
      <DefaultHeader>{formDefinition.formTitle}</DefaultHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        {renderInputs()}
        <ButtonByAdam
          id={formDefinition.submitBtn.id}
          type='submit'
          className={styles.FormContainerButton}
        >
          {formDefinition.submitBtn.text}
        </ButtonByAdam>
      </form>
    </section>
  )
}
